import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Loader2, Lock, ShieldCheck } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

/**
 * The patient's one-tap payment page for an order the clinic built from their
 * prescription. The link itself is the credential — signed, short-lived, and
 * carrying nothing but the order id — so there is no login and no OTP to chase.
 */
export default function PayLink() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/pay/${token}`);
      setOrder(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [token]);

  const pay = async () => {
    setBusy(true);
    const ok = await openRazorpayCheckout({
      order: { ...order, razorpay_key_id: order.razorpay_key_id, razorpay_order_id: order.razorpay_order_id },
      prefill: { name: order.customer_name },
      onSuccess: async (resp) => {
        try {
          const { data } = await api.post(`/pay/${token}/verify`, {
            order_id: order.id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_signature: resp.razorpay_signature,
          });
          setOrder((o) => ({ ...o, ...data }));
          toast.success("Payment received — thank you!");
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
        } finally {
          setBusy(false);
        }
      },
      onFailure: (reason) => { toast.error(reason); setBusy(false); },
      onDismiss: () => setBusy(false),
    });
    if (!ok) setBusy(false);
  };

  if (error) {
    return (
      <main className="max-w-md mx-auto px-6 py-24 text-center" data-testid="pay-link-error">
        <h1 className="font-display text-3xl mb-3">This link cannot be opened</h1>
        <p className="text-muted-foreground">{error}</p>
      </main>
    );
  }
  if (!order) {
    return (
      <main className="max-w-md mx-auto px-6 py-24 text-center text-muted-foreground" data-testid="pay-link-loading">
        <Loader2 className="animate-spin mx-auto mb-3" /> Loading your order…
      </main>
    );
  }

  const paid = order.payment_status === "paid";

  return (
    <main className="max-w-lg mx-auto px-5 sm:px-6 py-10 sm:py-14" data-testid="pay-link-page">
      <div className="overline mb-2">Immortal Vedas</div>
      <h1 className="font-display text-4xl mb-2" data-testid="pay-link-title">
        {paid ? "Payment received" : `Namaste ${order.customer_name.split(" ")[0]}`}
      </h1>
      <p className="text-muted-foreground mb-8">
        {paid
          ? "Thank you. Your medicines are being packed and you will get a WhatsApp update when they are dispatched."
          : "Your prescription order is ready. Please check the details and pay securely below."}
      </p>

      <Card className="border-border mb-6">
        <CardContent className="p-5 space-y-3">
          {order.items.map((item, i) => (
            <div key={`${item.name}-${i}`} className="flex items-start justify-between gap-4" data-testid={`pay-item-${i}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium">{item.name} × {item.quantity}</div>
                {item.instructions && <div className="text-xs text-muted-foreground">{item.instructions}</div>}
              </div>
              <div className="text-sm whitespace-nowrap">{money(item.price * item.quantity)}</div>
            </div>
          ))}

          <div className="border-t border-border pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Items</span><span>{money(order.gross_subtotal)}</span>
            </div>
            {order.discount_amount > 0 && (
              <div className="flex justify-between text-primary">
                <span>Discount</span><span>−{money(order.discount_amount)}</span>
              </div>
            )}
            {order.tax_amount > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>GST</span><span>{money(order.tax_amount)}</span>
              </div>
            )}
            <div className="flex justify-between font-display text-2xl pt-1" data-testid="pay-link-total">
              <span>Total</span><span>{money(order.total)}</span>
            </div>
          </div>

          {order.address && (
            <p className="text-xs text-muted-foreground border-t border-border pt-3" data-testid="pay-link-address">
              Delivering to: {order.address}
            </p>
          )}
          {order.has_backorder && !paid && (
            <p className="text-xs text-secondary" data-testid="pay-link-backorder">
              One item is being restocked — the clinic will call you about the delivery date.
            </p>
          )}
        </CardContent>
      </Card>

      {paid ? (
        <div className="flex items-center gap-2 text-primary" data-testid="pay-link-paid">
          <CheckCircle2 size={18} /> Paid{order.invoice_number ? ` · invoice ${order.invoice_number}` : ""}
        </div>
      ) : (
        <>
          <Button onClick={pay} disabled={busy} className="btn-action w-full h-12" data-testid="pay-link-pay">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Lock size={16} className="mr-2" />}
            {busy ? "Opening payment…" : `Pay ${money(order.total)} securely`}
          </Button>
          <p className="text-xs text-muted-foreground flex items-center gap-2 justify-center mt-3">
            <ShieldCheck size={12} /> Secured by Razorpay · UPI, Card & Netbanking
          </p>
        </>
      )}
    </main>
  );
}
